import { useCallback, useState } from 'react';

export interface YouTubeVideoInfo {
  id: string;
  title: string;
  description: string;
  thumbnail: {
    default: string;
    medium: string;
    high: string;
    standard?: string;
    maxres?: string;
  };
  channelTitle: string;
  publishedAt: string;
  duration: string;
  viewCount: string;
}

export const extractYouTubeId = (url: string): string | null => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/, // Regular YouTube videos
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/, // Videos with additional params
    /(?:youtube\.com\/shorts\/|youtu\.be\/shorts\/)([^&\n?#]+)/, // YouTube Shorts
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      return match[1] ?? null;
    }
  }

  return null;
};

export const isValidYouTubeUrl = (url: string): boolean => {
  return extractYouTubeId(url) !== null;
};

export const getYouTubeVideoInfo = async (
  videoId: string,
  apiKey: string
): Promise<YouTubeVideoInfo | null> => {
  try {
    const response = await fetch(
      `https://www.googleapis.com/youtube/v3/videos?id=${videoId}&key=${apiKey}&part=snippet,statistics,contentDetails`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch video info');
    }

    const data = await response.json();

    if (!data.items || data.items.length === 0) {
      return null;
    }

    const video = data.items[0];

    return {
      id: videoId,
      title: video.snippet.title,
      description: video.snippet.description,
      thumbnail: {
        default: video.snippet.thumbnails.default?.url || '',
        medium: video.snippet.thumbnails.medium?.url || '',
        high: video.snippet.thumbnails.high?.url || '',
        standard: video.snippet.thumbnails.standard?.url,
        maxres: video.snippet.thumbnails.maxres?.url,
      },
      channelTitle: video.snippet.channelTitle,
      publishedAt: video.snippet.publishedAt,
      duration: video.contentDetails.duration,
      viewCount: video.statistics.viewCount,
    };
  } catch (error) {
    console.error('Error fetching YouTube video info:', error);
    return null;
  }
};

export const getYouTubeVideoInfoNoAPI = async (
  videoId: string
): Promise<Partial<YouTubeVideoInfo> | null> => {
  try {
    const response = await fetch(
      `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch video info');
    }

    const data = await response.json();

    return {
      id: videoId,
      title: data.title,
      thumbnail: {
        default: data.thumbnail_url,
        medium: data.thumbnail_url,
        high: data.thumbnail_url,
      },
      channelTitle: data.author_name,
      publishedAt: '',
      duration: '',
      viewCount: '',
      description: '',
    };
  } catch (error) {
    console.error('Error fetching YouTube video info (no API):', error);
    return null;
  }
};

export async function getYouTubeVideoInfoViaServer(videoId: string) {
  const res = await fetch(
    `/api/youtube/video-info?videoId=${encodeURIComponent(videoId)}`
  );
  if (!res.ok) return null;
  return res.json() as Promise<{
    id: string;
    title: string;
    description: string;
    thumbnail: {
      default: string;
      medium: string;
      high: string;
      standard?: string;
      maxres?: string;
    };
    channelTitle: string;
    publishedAt: string;
    duration: string;
    viewCount: string;
  }>;
}

export const getYouTubeEmbedUrl = (
  videoId: string,
  options?: {
    autoplay?: boolean;
    start?: number;
    end?: number;
    mute?: boolean;
  }
): string => {
  const baseUrl = `https://www.youtube.com/embed/${videoId}`;
  const params = new URLSearchParams();

  if (options?.autoplay) params.append('autoplay', '1');
  if (options?.start) params.append('start', options.start.toString());
  if (options?.end) params.append('end', options.end.toString());
  if (options?.mute) params.append('mute', '1');

  params.append('rel', '0');
  params.append('modestbranding', '1');

  return `${baseUrl}?${params.toString()}`;
};

export function getYouTubeThumbnail(url: string) {
  const regExp =
    /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  const id = match && match[2] && match[2].length === 11 ? match[2] : null;
  if (!id) return null;
  return `https://img.youtube.com/vi/${id}/maxresdefault.jpg`;
}

export const useYouTubeVideo = (initialUrl?: string) => {
  const [videoUrl, setVideoUrl] = useState(initialUrl || '');
  const [videoInfo, setVideoInfo] = useState<YouTubeVideoInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadVideoInfo = useCallback(async (url: string) => {
    const videoId = extractYouTubeId(url);

    if (!videoId) {
      setError('Invalid YouTube URL');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const infoFromApi = await getYouTubeVideoInfoViaServer(videoId);

      if (infoFromApi) {
        setVideoInfo(infoFromApi);
        return;
      }

      const fallback = await getYouTubeVideoInfoNoAPI(videoId);
      if (fallback) {
        setVideoInfo(fallback as YouTubeVideoInfo);
        return;
      }

      setError('Could not load video information');
    } catch {
      setError('Failed to load video information');
    } finally {
      setLoading(false);
    }
  }, []);

  const updateUrl = useCallback(
    (url: string) => {
      setVideoUrl(url);

      if (url && isValidYouTubeUrl(url)) {
        loadVideoInfo(url);
      } else {
        setVideoInfo(null);
        setError(url ? 'Invalid YouTube URL' : null);
      }
    },
    [loadVideoInfo]
  );

  return {
    videoUrl,
    videoInfo,
    loading,
    error,
    updateUrl,
    isValid: videoInfo !== null,
    videoId: videoInfo?.id || extractYouTubeId(videoUrl),
  };
};